import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MapPin, Plus, Trash2, Edit2, Save, X, Building2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to handle map clicks
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Component to update map center
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

const OfficeLocations = () => {
  const { user } = useAuth();
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: 14.5995, // Default: Manila
    longitude: 120.9842
  });

  // Map state
  const [mapCenter, setMapCenter] = useState([14.5995, 120.9842]);
  const [markerPosition, setMarkerPosition] = useState(null);

  useEffect(() => {
    fetchOffices();
  }, []);

  const fetchOffices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/offices`);
      setOffices(res.data);
    } catch (error) {
      toast.error('Failed to load offices');
    }
    setLoading(false);
  };

  const handleLocationSelect = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
    setMarkerPosition([lat, lng]);
  };

  const searchAddress = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      // Using Nominatim (OpenStreetMap) for geocoding - free, no API key needed
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`
      );
      const data = await res.json();
      setSearchResults(data);
    } catch (error) {
      toast.error('Search failed');
    }
    setSearching(false);
  };

  const selectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setFormData(prev => ({
      ...prev,
      address: result.display_name,
      latitude: lat,
      longitude: lng
    }));
    setMarkerPosition([lat, lng]);
    setMapCenter([lat, lng]);
    setSearchResults([]);
    setSearchQuery('');
  };

  const openCreateDialog = () => {
    setEditingOffice(null);
    setFormData({
      name: '',
      address: '',
      latitude: 14.5995,
      longitude: 120.9842
    });
    setMarkerPosition(null);
    setMapCenter([14.5995, 120.9842]);
    setShowDialog(true);
  };

  const openEditDialog = (office) => {
    setEditingOffice(office);
    setFormData({
      name: office.name,
      address: office.address,
      latitude: office.latitude,
      longitude: office.longitude
    });
    setMarkerPosition([office.latitude, office.longitude]);
    setMapCenter([office.latitude, office.longitude]);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Office name is required');
      return;
    }
    if (!markerPosition) {
      toast.error('Please select a location on the map');
      return;
    }

    try {
      if (editingOffice) {
        await axios.put(`${API}/offices/${editingOffice.id}`, formData);
        toast.success('Office updated successfully');
      } else {
        await axios.post(`${API}/offices`, formData);
        toast.success('Office created successfully');
      }
      setShowDialog(false);
      fetchOffices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save office');
    }
  };

  const handleDelete = async (officeId) => {
    if (!window.confirm('Are you sure you want to delete this office?')) return;
    
    try {
      await axios.delete(`${API}/offices/${officeId}`);
      toast.success('Office deleted');
      fetchOffices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete office');
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-red-500">Admin access required</p>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Office Locations</h1>
          <p className="text-slate-500">Manage office locations for geofenced clock-in</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-office-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Office
        </Button>
      </div>

      {/* Office List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      ) : offices.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No Office Locations</h3>
            <p className="text-slate-500 mb-4">Add your first office location to enable geofenced clock-in</p>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" /> Add First Office
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {offices.map(office => (
            <Card key={office.id} className="hover:shadow-lg transition-shadow" data-testid={`office-card-${office.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{office.name}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2">{office.address}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 text-xs text-slate-400">
                  <p>Lat: {office.latitude.toFixed(6)}</p>
                  <p>Lng: {office.longitude.toFixed(6)}</p>
                </div>

                <div className="mt-4 flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openEditDialog(office)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDelete(office.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Office Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="text-blue-500" />
              {editingOffice ? 'Edit Office' : 'Add New Office'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Office Name */}
            <div>
              <label className="text-sm font-medium text-slate-700">Office Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Main Office, Branch 1"
                data-testid="office-name-input"
              />
            </div>

            {/* Address Search */}
            <div>
              <label className="text-sm font-medium text-slate-700">Search Address</label>
              <div className="flex gap-2">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for an address..."
                  onKeyDown={(e) => e.key === 'Enter' && searchAddress()}
                />
                <Button 
                  variant="outline" 
                  onClick={searchAddress}
                  disabled={searching}
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 border rounded-lg bg-white shadow-lg max-h-40 overflow-y-auto">
                  {searchResults.map((result, idx) => (
                    <button
                      key={idx}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 border-b last:border-b-0"
                      onClick={() => selectSearchResult(result)}
                    >
                      {result.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Address (Manual) */}
            <div>
              <label className="text-sm font-medium text-slate-700">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Full address"
              />
            </div>

            {/* Map */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Click on the map to set location *
              </label>
              <div className="h-[300px] rounded-lg overflow-hidden border">
                <MapContainer
                  center={mapCenter}
                  zoom={15}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler onLocationSelect={handleLocationSelect} />
                  <MapCenterUpdater center={mapCenter} />
                  {markerPosition && (
                    <Marker position={markerPosition} />
                  )}
                </MapContainer>
              </div>
            </div>

            {/* Coordinates Display */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Latitude</label>
                <Input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => {
                    const lat = parseFloat(e.target.value);
                    setFormData(prev => ({ ...prev, latitude: lat }));
                    if (markerPosition) {
                      setMarkerPosition([lat, markerPosition[1]]);
                    }
                  }}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Longitude</label>
                <Input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => {
                    const lng = parseFloat(e.target.value);
                    setFormData(prev => ({ ...prev, longitude: lng }));
                    if (markerPosition) {
                      setMarkerPosition([markerPosition[0], lng]);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-office-btn">
              <Save className="h-4 w-4 mr-2" />
              {editingOffice ? 'Update Office' : 'Create Office'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfficeLocations;
